import React from "react";
import { Link } from "react-router-dom";
import TopNav from "./TopNav";
import SidebarAds from "./SidebarAds";
import { Mail } from "lucide-react";

const PRODUCT_LINKS = [
  { label: "Compare prices", href: "/compare" },
  { label: "AI Name Studio", href: "/ai-finder" },
  { label: "Premium domains", href: "/premium-domains" },
  { label: "Pricing", href: "/pricing" },
];

const GUIDE_LINKS = [
  { label: "Cheap domains", href: "/cheap-domains" },
  { label: "Cheapest domain registration", href: "/cheapest-domain-registration" },
  { label: "Cheap domain & hosting", href: "/cheap-domain-and-hosting" },
  { label: "Cheap domain & email", href: "/cheap-domain-and-email" },
  { label: "Cheapest .com domain", href: "/cheapest-com-domain" },
  { label: "Domain price comparison", href: "/domain-price-comparison" },
  { label: "Buy domain cheap", href: "/buy-domain-cheap" },
  { label: "Best cheap domain providers", href: "/cheap-domain-provider" },
  { label: "Best .ai domains", href: "/best-ai-domains" },
  { label: "Startup naming playbook", href: "/startup-domain-names" },
];

const COMPANY_LINKS = [
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
  { label: "Blog", href: "/blog" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", href: "/privacy" },
  { label: "Terms of Service", href: "/terms" },
  { label: "Cookie Policy", href: "/cookies" },
  { label: "Sitemap", href: "/sitemap" },
];

/**
 * 3-column ad-supported app layout.
 * Left ads | Main content | Right ads.
 * On mobile, ads stack below the main content.
 */
export default function Layout({ children, hideSidebars = false }) {
  return (
    <div className="min-h-screen bg-white" data-testid="app-layout">
      <TopNav />
      <main className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-12">
        {hideSidebars ? (
          <div className="w-full">{children}</div>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[176px_minmax(0,1fr)_240px] xl:gap-10">
            <div className="hidden lg:block">
              <SidebarAds slot="left" />
            </div>
            <div className="min-w-0">{children}</div>
            <div className="hidden lg:block">
              <SidebarAds slot="right" />
            </div>
            <div className="lg:hidden">
              <SidebarAds slot="right" />
            </div>
          </div>
        )}
      </main>

      <footer
        className="mt-16 border-t border-zinc-200 bg-zinc-50/60"
        data-testid="site-footer"
      >
        <div className="mx-auto max-w-[1440px] px-4 py-12 md:px-8 md:py-16">
          {/* Top: brand + columns */}
          <div className="grid gap-10 md:grid-cols-12">
            {/* Brand */}
            <div className="md:col-span-4">
              <Link to="/" className="flex items-center gap-2">
                <img src="/logo.png" alt="Dompryce" className="h-8 w-8 rounded-md" />
                <span className="font-display text-base font-semibold text-zinc-900">Dompryce</span>
              </Link>
              <p className="mt-4 max-w-xs text-sm leading-relaxed text-zinc-600">
                The honest domain price comparison engine. Live rates from various hosts, an AI-powered name studio, and curated premium inventory — all in one place.
              </p>
              <a
                href="mailto:support@dompryce.com"
                className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-zinc-900 hover:text-indigo-600"
                data-testid="footer-email"
              >
                <Mail className="h-4 w-4" /> support@dompryce.com
              </a>
            </div>

            {/* Link columns */}
            <div className="md:col-span-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Product</div>
              <ul className="mt-4 space-y-2.5">
                {PRODUCT_LINKS.map((l) => (
                  <li key={l.href}>
                    <Link to={l.href} className="text-sm text-zinc-600 hover:text-zinc-900" data-testid={`footer-link-${l.href}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Guides</div>
              <ul className="mt-4 space-y-2.5">
                {GUIDE_LINKS.slice(0, 8).map((l) => (
                  <li key={l.href}>
                    <Link to={l.href} className="text-sm text-zinc-600 hover:text-zinc-900" data-testid={`footer-link-${l.href}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-3">
              <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Company</div>
              <ul className="mt-4 space-y-2.5">
                {COMPANY_LINKS.map((l) => (
                  <li key={l.href}>
                    <Link to={l.href} className="text-sm text-zinc-600 hover:text-zinc-900" data-testid={`footer-link-${l.href}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-8 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Legal</div>
              <ul className="mt-4 space-y-2.5">
                {LEGAL_LINKS.map((l) => (
                  <li key={l.href}>
                    <Link to={l.href} className="text-sm text-zinc-600 hover:text-zinc-900" data-testid={`footer-link-${l.href}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-zinc-200 pt-6 text-xs text-zinc-500 md:flex-row md:items-center">
            <div>© {new Date().getFullYear()} Dompryce. All rights reserved.</div>
            <div className="flex flex-wrap gap-4">
              <Link to="/privacy" className="hover:text-zinc-900">Privacy</Link>
              <Link to="/terms" className="hover:text-zinc-900">Terms</Link>
              <Link to="/cookies" className="hover:text-zinc-900">Cookies</Link>
              <Link to="/sitemap" data-testid="footer-sitemap" className="hover:text-zinc-900">Sitemap</Link>
              <Link to="/contact" className="hover:text-zinc-900">Contact</Link>
            </div>
          </div>
          <p className="mt-4 max-w-3xl text-[11px] leading-relaxed text-zinc-400">
            Dompryce is an independent price comparison service. Registrar logos and trademarks belong to their respective owners. Some outbound links may contain affiliate tags — affiliate relationships never influence rankings or the price you pay.
          </p>
        </div>
      </footer>
    </div>
  );
}
